import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { useLocalStorage } from "@/hooks/useLocalStorage";
import { logger } from "@/util/logger";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface UsePWAInstallReturn {
  isInstallable: boolean;
  promptInstall: () => Promise<void>;
  isDismissed: boolean;
  dismissPrompt: () => void;
  isInstalled: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  showManualInstructions: boolean;
}

const STORAGE_KEY = "lossdog-pwa-install-dismissed";

// Schema for localStorage value
const dismissedSchema = z.boolean();

/**
 * Hook to manage PWA installation state and prompt
 * Handles beforeinstallprompt event (Android/Chrome) and tracks user preferences
 * Also detects iOS devices for manual installation instructions
 * Uses localStorage for dismissal state in all environments
 * @returns Object containing install state and actions
 */
export const usePWAInstall = (): UsePWAInstallReturn => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  // Use localStorage for all environments
  const { value: isDismissed, set: setDismissed } = useLocalStorage({
    key: STORAGE_KEY,
    schema: dismissedSchema,
  });

  // Detect iOS and Android devices
  useEffect(() => {
    if (typeof window !== "undefined") {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      const isAndroidDevice = /android/.test(userAgent);

      setIsIOS(isIOSDevice);
      setIsAndroid(isAndroidDevice);

      logger.info(`PWA: Device detection - iOS: ${isIOSDevice}, Android: ${isAndroidDevice}`);
    }
  }, []);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      logger.info("PWA: beforeinstallprompt event fired");
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  // Listen for appinstalled event
  useEffect(() => {
    const handleAppInstalled = () => {
      logger.info("PWA: App installed successfully");
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Check if app is already installed (running in standalone mode)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

      if (isStandalone) {
        logger.info("PWA: App is already running in standalone mode");
        setIsInstalled(true);
      }
    }
  }, []);

  // Function to trigger the install prompt
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      logger.warn("PWA: Install prompt not available");
      return;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      logger.info(`PWA: User response to install prompt: ${outcome}`);

      if (outcome === "accepted") {
        setIsInstalled(true);
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
    } catch (error) {
      logger.error("PWA: Error showing install prompt", error);
    }
  }, [deferredPrompt]);

  // Function to dismiss the prompt permanently
  const dismissPrompt = useCallback(() => {
    logger.info("PWA: User dismissed install prompt");
    setDismissed(true);
  }, [setDismissed]);

  return {
    isInstallable: !!deferredPrompt,
    promptInstall,
    isDismissed: isDismissed ?? false,
    dismissPrompt,
    isInstalled,
    isIOS,
    isAndroid,
    showManualInstructions: isAndroid && !deferredPrompt && !isInstalled,
  };
};
